import { Link2, FileText } from 'lucide-react'
import { Tag, VaultItem } from '../../config'
import { trackPointer } from '../../lib/folderColors'
import { domainOf, excerptOf, formatDate } from '../../lib/items'

interface ItemGridProps {
  items: VaultItem[]
  tags: Tag[]
  folderNames: Map<string, string>
  showFolder: boolean
  onOpen: (item: VaultItem) => void
}

export function ItemGrid({
  items,
  tags,
  folderNames,
  showFolder,
  onOpen,
}: ItemGridProps) {
  const tagById = new Map(tags.map((t) => [t.id, t]))

  return (
    <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(232px,1fr))] sm:gap-3.5">
      {items.map((item, i) => {
        const isBookmark = item.type === 'bookmark'
        const itemTags = item.tagIds
          .map((id) => tagById.get(id))
          .filter((t): t is Tag => !!t)
        const footer = isBookmark
          ? domainOf(item.content)
          : showFolder && item.folderId
            ? (folderNames.get(item.folderId) ?? 'Note')
            : 'Note'
        return (
          <li
            key={item.id}
            className="animate-spring-up"
            style={{ animationDelay: `${120 + Math.min(i, 12) * 45}ms` }}
          >
            <button
              type="button"
              className="item-card"
              onClick={() => onOpen(item)}
              onPointerMove={trackPointer}
            >
              <span className="flex items-center justify-between">
                <span
                  className={`kind-ic ${isBookmark ? 'bm' : 'note'}`}
                  aria-hidden="true"
                >
                  {isBookmark ? (
                    <Link2 className="h-[18px] w-[18px]" />
                  ) : (
                    <FileText className="h-[18px] w-[18px]" />
                  )}
                </span>
                <span className="text-[12.5px] text-faint">
                  {formatDate(item.updatedAt)}
                </span>
              </span>
              <span className="sr-only">
                {isBookmark ? 'Bookmark:' : 'Note:'}
              </span>
              <span className="line-clamp-2 font-display text-[15px] font-semibold leading-snug tracking-tight sm:text-[16.5px]">
                {item.title}
              </span>
              <span className="line-clamp-2 break-words text-[13.5px] leading-relaxed text-muted sm:text-sm">
                {excerptOf(item)}
              </span>
              <span className="mt-auto flex items-center justify-between gap-2 pt-1 text-[12.5px] text-faint">
                <span className="min-w-0 truncate font-mono">{footer}</span>
                {itemTags.length > 0 && (
                  <span
                    className="flex flex-none gap-1"
                    aria-label={`Tags: ${itemTags.map((t) => t.name).join(', ')}`}
                  >
                    {itemTags.slice(0, 4).map((t) => (
                      <span
                        key={t.id}
                        className="dot"
                        style={{ background: t.color }}
                      />
                    ))}
                  </span>
                )}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
